/**
 * Push Notification Analytics Tab
 * Shows delivery trends, peak hours, day performance, template performance, subscriber growth
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Clock, Calendar, TrendingUp, FileText, BarChart3,
  ArrowUp, ArrowDown, Activity, CheckCircle, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/utils/apiClient';

export default function PushAnalyticsTab() {
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [deliveryTrend, setDeliveryTrend] = useState([]);
  const [peakHours, setPeakHours] = useState(null);
  const [templatePerformance, setTemplatePerformance] = useState([]);
  const [subscriberGrowth, setSubscriberGrowth] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, trendRes, peakRes, templateRes, growthRes, dowRes] = await Promise.all([
        api.get('/push/analytics/overview'),
        api.get('/push/analytics/delivery-trend', { params: { days: 14 } }),
        api.get('/push/analytics/peak-hours', { params: { days: 30 } }),
        api.get('/push/analytics/template-performance', { params: { days: 30 } }),
        api.get('/push/analytics/subscriber-growth', { params: { days: 30 } }),
        api.get('/push/analytics/day-of-week', { params: { days: 30 } })
      ]);
      
      setAnalyticsOverview(overviewRes.data);
      setDeliveryTrend(trendRes.data.trend || []);
      setPeakHours(peakRes.data);
      setTemplatePerformance(templateRes.data.templates || []);
      setSubscriberGrowth(growthRes.data);
      setDayOfWeek(dowRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading && !analyticsOverview) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      {analyticsOverview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{analyticsOverview.total_sent || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Sent (30d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{analyticsOverview.avg_delivery_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Avg Delivery Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{analyticsOverview.avg_click_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Avg Click Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{analyticsOverview.broadcasts_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Broadcasts (30d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* First Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Trend */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-base">Delivery Trend (14 days)</CardTitle>
            <CardDescription>Daily notification deliveries</CardDescription>
          </CardHeader>
          <CardContent>
            {deliveryTrend.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-end gap-1 h-32">
                  {deliveryTrend.map((day, idx) => {
                    const maxVal = Math.max(...deliveryTrend.map(d => d.sent || 1));
                    const height = (day.sent / maxVal) * 100;
                    return (
                      <div 
                        key={idx}
                        className="flex-1 flex flex-col items-center justify-end"
                      >
                        <div 
                          className="w-full bg-primary/70 rounded-t hover:bg-primary transition-all"
                          style={{ height: `${Math.max(height, 5)}%` }}
                          title={`${day.date}: ${day.sent} sent, ${day.delivered} delivered`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{deliveryTrend[0]?.date?.slice(5)}</span>
                  <span>{deliveryTrend[deliveryTrend.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Peak Hours */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-base">Peak Engagement Hours</CardTitle>
            <CardDescription>Best times to send notifications</CardDescription>
          </CardHeader>
          <CardContent>
            {peakHours?.hours ? (
              <div className="space-y-3">
                <div className="flex gap-0.5 h-24">
                  {peakHours.hours.map((hour) => {
                    const maxVal = Math.max(...peakHours.hours.map(h => h.engagement || 1));
                    const height = (hour.engagement / maxVal) * 100;
                    const isPeak = peakHours.best_engagement_hours?.includes(hour.hour);
                    return (
                      <div
                        key={hour.hour}
                        className="flex-1 flex flex-col justify-end"
                        title={`${hour.hour}:00 - ${hour.engagement} engagement, ${hour.delivery_rate}% delivery`}
                      >
                        <div 
                          className={`rounded-t transition-all ${isPeak ? 'bg-green-500' : 'bg-primary/50 hover:bg-primary/70'}`}
                          style={{ height: `${Math.max(height, 3)}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>12AM</span>
                  <span>6AM</span>
                  <span>12PM</span>
                  <span>6PM</span>
                  <span>11PM</span>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">Best times to send:</p>
                  <div className="flex flex-wrap gap-2">
                    {peakHours.best_engagement_hours?.slice(0, 3).map((h) => (
                      <Badge key={h} variant="secondary" className="text-xs">
                        Best: {h}:00
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day of Week Performance */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-base">Day of Week Performance</CardTitle>
            <CardDescription>
              {dayOfWeek?.best_day && <span className="text-green-500">Best: {dayOfWeek.best_day}</span>}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dayOfWeek?.days ? (
              <div className="space-y-2">
                {dayOfWeek.days.map((day) => {
                  const maxSent = Math.max(...dayOfWeek.days.map(d => d.sent || 1));
                  const width = (day.sent / maxSent) * 100;
                  return (
                    <div key={day.day_index} className="flex items-center gap-3">
                      <span className="w-12 text-sm text-muted-foreground">{day.name.slice(0, 3)}</span>
                      <div className="flex-1 h-6 bg-muted/30 rounded overflow-hidden">
                        <div 
                          className="h-full bg-primary/70 flex items-center justify-end pr-2 rounded transition-all"
                          style={{ width: `${Math.max(width, 2)}%` }}
                        >
                          {day.sent > 0 && (
                            <span className="text-xs text-white font-medium">{day.sent}</span>
                          )}
                        </div>
                      </div>
                      <span className="w-12 text-xs text-right text-muted-foreground">
                        {day.engagement_rate}%
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template Performance */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-base">Template Performance</CardTitle>
            <CardDescription>Top performing notification types</CardDescription>
          </CardHeader>
          <CardContent>
            {templatePerformance.length > 0 ? (
              <div className="space-y-3">
                {templatePerformance.slice(0, 6).map((template, idx) => (
                  <div key={template.template_key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-foreground truncate max-w-[150px]">
                        {template.template_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{template.sent} sent</span>
                      <Badge 
                        variant={template.click_rate > 5 ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {template.click_rate}% CTR
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscriber Growth */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-foreground text-base">Subscriber Growth (30 days)</CardTitle>
            <CardDescription>
              {subscriberGrowth?.metrics && (
                <span className="flex items-center gap-2">
                  <span>+{subscriberGrowth.metrics.total_new} new</span>
                  {subscriberGrowth.metrics.week_over_week_growth !== 0 && (
                    <span className={`flex items-center gap-1 ${subscriberGrowth.metrics.week_over_week_growth > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {subscriberGrowth.metrics.week_over_week_growth > 0 ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )}
                      {Math.abs(subscriberGrowth.metrics.week_over_week_growth)}% WoW
                    </span>
                  )}
                </span>
              )}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {subscriberGrowth?.trend ? (
            <div className="space-y-2">
              <div className="flex items-end gap-0.5 h-24">
                {subscriberGrowth.trend.slice(-30).map((day, idx) => {
                  const maxVal = Math.max(...subscriberGrowth.trend.slice(-30).map(d => d.total_subscribers || 1));
                  const minVal = Math.min(...subscriberGrowth.trend.slice(-30).map(d => d.total_subscribers || 0));
                  const range = maxVal - minVal || 1;
                  const height = ((day.total_subscribers - minVal) / range) * 100;
                  return (
                    <div 
                      key={idx} 
                      className="flex-1 bg-primary/60 rounded-t hover:bg-primary transition-all"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${day.date}: ${day.total_subscribers} total (+${day.new_subscribers} new)`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{subscriberGrowth.trend.slice(-30)[0]?.date?.slice(5)}</span>
                <span className="font-medium text-foreground">
                  {subscriberGrowth.trend.slice(-1)[0]?.total_subscribers || 0} total
                </span>
                <span>{subscriberGrowth.trend.slice(-1)[0]?.date?.slice(5)}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No growth data yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
